import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { fulfillmentApi } from '../api/fulfillment-api';
import { AppliedFilters } from '../components/AppliedFilters';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  dispatchGroup,
  dispatchGroupLabels,
  dispatchGroups,
  isOpenOrder,
  orderProgress,
  orderTitle,
  type FulfillmentOrder,
} from '../domain/fulfillment';
import { NewFulfillmentOrderDrawer } from '../features/fulfillment/NewFulfillmentOrderDrawer';
import { OrderStateBadge, ProgressMeter } from '../features/fulfillment/OrderStateBadge';
import { useFulfillmentReferences } from '../features/fulfillment/use-fulfillment-references';

const views = ['open', 'completed', 'cancelled', 'all'] as const;
type View = (typeof views)[number];
const viewLabels: Record<View, string> = {
  open: 'Open',
  completed: 'Packed',
  cancelled: 'Cancelled',
  all: 'All',
};

function isView(value: string | null): value is View {
  return views.some((view) => view === value);
}

function inView(order: FulfillmentOrder, view: View): boolean {
  switch (view) {
    case 'open':
      return isOpenOrder(order);
    case 'completed':
      return order.state === 'COMPLETED';
    case 'cancelled':
      return order.state === 'CANCELLED';
    case 'all':
      return true;
  }
}

/** The pick-pack queue: open orders by dispatch date (tidaltech/koi#8084). */
export function FulfillmentPage() {
  const navigate = useNavigate();
  const references = useFulfillmentReferences();
  const [creating, setCreating] = useState(false);
  const [params, setParams] = useSearchParams();
  const viewParam = params.get('view');
  const view: View = isView(viewParam) ? viewParam : 'open';
  const siteFilter = params.get('site') ?? '';
  const query = params.get('q') ?? '';

  const orders = useQuery({
    queryKey: ['fulfillment-orders'],
    queryFn: () => fulfillmentApi.listOrders(),
  });

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (orders.data ?? [])
      .filter((order) => inView(order, view))
      .filter((order) => !siteFilter || order.siteId === siteFilter)
      .filter(
        (order) =>
          !needle ||
          `${orderTitle(order)} ${references.siteName(order.siteId)} ${references.customerName(order.customerId)}`
            .toLowerCase()
            .includes(needle),
      )
      .sort((a, b) => (a.dispatchDate ?? '￿').localeCompare(b.dispatchDate ?? '￿'));
  }, [orders.data, view, siteFilter, query, references]);

  const groups = useMemo(() => {
    const now = new Date();
    return dispatchGroups
      .map((group) => ({
        group,
        orders: filtered.filter((order) => dispatchGroup(order.dispatchDate, now) === group),
      }))
      .filter((entry) => entry.orders.length > 0);
  }, [filtered]);

  function setParam(name: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next);
  }

  if (orders.isLoading) return <PageLoadingState label="Loading orders…" />;
  if (orders.isError) {
    return (
      <ErrorState
        title="Orders could not be loaded"
        rpcName="ListFulfillmentOrders"
        onRetry={() => void orders.refetch()}
      />
    );
  }

  const siteIds = [...new Set((orders.data ?? []).map((order) => order.siteId))];
  const activeFilters = [
    query.trim()
      ? { key: 'q', label: `Search: ${query.trim()}`, onRemove: () => setParam('q', '') }
      : null,
    siteFilter
      ? {
          key: 'site',
          label: `Site: ${references.siteName(siteFilter)}`,
          onRemove: () => setParam('site', ''),
        }
      : null,
  ].filter((f): f is { key: string; label: string; onRemove: () => void } => f !== null);

  return (
    <>
      <PageHeader
        title="Pick & pack"
        description="Orders to pick and pack, soonest dispatch first."
        actions={
          <>
            <Link to="/fulfillment/templates" className="button-link">
              Templates
            </Link>
            <button type="button" className="primary" onClick={() => setCreating(true)}>
              New order
            </button>
          </>
        }
      />
      {creating ? (
        <NewFulfillmentOrderDrawer
          onClose={() => setCreating(false)}
          onCreated={(order) => void navigate(`/fulfillment/${order.id}`)}
        />
      ) : null}

      <div className="region-tabs" role="tablist" aria-label="Order state">
        {views.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={view === candidate}
            className={`region-tab${view === candidate ? ' is-active' : ''}`}
            onClick={() => setParam('view', candidate === 'open' ? '' : candidate)}
          >
            {viewLabels[candidate]}{' '}
            <span className="tab-count">
              {(orders.data ?? []).filter((order) => inView(order, candidate)).length}
            </span>
          </button>
        ))}
      </div>

      <section className="reference-workspace" aria-label="Fulfillment orders">
        <form
          className="requests-controls"
          role="search"
          aria-label="Filter orders"
          onSubmit={(event) => event.preventDefault()}
        >
          <label className="search-field">
            <span>Search</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setParam('q', event.target.value)}
              placeholder="Order form number, site or customer"
              aria-label="Search orders"
            />
          </label>
          <label>
            <span>Site</span>
            <SearchableSelect
              ariaLabel="Filter by site"
              value={siteFilter}
              onChange={(value) => setParam('site', value)}
              options={[
                { value: '', label: 'All sites' },
                ...siteIds.map((id) => ({ value: id, label: references.siteName(id) })),
              ]}
              placeholder="All sites"
            />
          </label>
        </form>
        <AppliedFilters filters={activeFilters} />

        {filtered.length === 0 ? (
          <div className="empty-state">
            <strong>
              {(orders.data ?? []).length === 0
                ? 'No orders yet.'
                : 'No orders match these filters.'}
            </strong>
            {(orders.data ?? []).length === 0 ? (
              <button type="button" className="primary" onClick={() => setCreating(true)}>
                New order
              </button>
            ) : (
              <button type="button" onClick={() => setParams(new URLSearchParams())}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          groups.map(({ group, orders: groupOrders }) => (
            <section key={group} className="dispatch-group" aria-labelledby={`dispatch-${group}`}>
              <h2 id={`dispatch-${group}`} className={`dispatch-group-title dispatch-${group}`}>
                {dispatchGroupLabels[group]} <span className="tab-count">{groupOrders.length}</span>
              </h2>
              <ul className="order-cards">
                {groupOrders.map((order) => {
                  const progress = orderProgress(order);
                  return (
                    <li key={order.id} className="order-card">
                      <div className="order-card-heading">
                        <Link to={`/fulfillment/${order.id}`} className="order-card-title">
                          {orderTitle(order)}
                        </Link>
                        <OrderStateBadge state={order.state} />
                      </div>
                      <p className="order-card-site">
                        {references.siteName(order.siteId)} ·{' '}
                        {references.customerName(order.customerId)}
                      </p>
                      <div className="order-card-meta">
                        <span>
                          {order.dispatchDate
                            ? `Dispatch ${new Date(order.dispatchDate).toLocaleDateString()}`
                            : 'No dispatch date'}
                        </span>
                        <ProgressMeter
                          complete={progress.complete}
                          total={progress.total}
                          label={`${orderTitle(order)}: ${progress.complete} of ${progress.total} picked`}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </section>
    </>
  );
}
